import './styles.css';
import './home.css';
import './match-setup.css';
import './loading/loading.css';

import { BrowserIdentityService } from './auth/BrowserIdentityService';
import { gameAssetResources } from './assets/GameAssetResources';
import { InventoryApiClient, routePaymentReturn } from './inventory/InventoryApiClient';
import { botDefinition, configuredBotCatalog, installBotCatalogSnapshot } from './game/bots/BotCatalog';
import { fetchBotCatalogSnapshot } from './game/bots/BotCatalogClient';
import { SIM_UNIT_METERS } from './game/config';
import { AudioRuntime } from './game/audio/AudioRuntime';
import { HttpMusicSelectionSource } from './game/audio/MusicSelection';
import type { GameCameraState, GameReplayState, GameSpectatorState, GameSessionCallbacks } from './game/GameSession';
import { InputBindingStore } from './game/input/InputBindingStore';
import { CameraSettingsStore } from './game/camera/CameraSettingsStore';
import type { SessionInteraction } from './game/input/SessionInteraction';
import { inputBindingLabel } from './game/input/InputBindings';
import { RoomClient } from './online/RoomClient';
import { OnlineMatchHud } from './online/OnlineMatchHud';
import { matchSessionActionKind } from './online/MatchSessionAction';
import { SessionMenu } from './ui/SessionMenu';
import { SessionNavigationGuard } from './ui/SessionNavigationGuard';
import { RankedRecoveryPrompt } from './online/RankedRecoveryPrompt';
import { buildOnlineMatchDefinition } from './online/OnlineMatchDefinition';
import { mountGlobalChat } from './online/MenuOnlineServices';
import { MatchmakingView } from './online/MatchmakingView';
import type { QueueTicket, RankedRecoveryAuthorization } from './online/MatchmakingContracts';
import { matchRosterLatencyQuality, type MatchRosterState } from './game/presentation/MatchRosterPresentation';
import type { EntityId, MatchConfig, MatchState, Team } from './game/types';
import { createMatchDefinition } from './game/world/MatchDefinition';
import { formatNumber, t } from './i18n';
import { localizedErrorMessage } from './i18n/presentation';
import { LoadingScreen } from './loading/LoadingScreen';
import {
  MATCH_SETUP_TEAM_CAPACITY,
  botAvailableForMatchSetup,
  canPlaceBot,
  createMatchSetupState,
  matchConfigFromSetup,
  matchSetupLocalTeam,
  matchSetupReady,
  matchSetupTeamSize,
  moveMatchSetupParticipant,
  placeBot,
  placeLocalPlayer,
  removeMatchSetupParticipant,
  setMatchSetupMode,
  type MatchSetupState,
} from './game/world/MatchSetup';

const BOOST_METER_RADIUS = 46;
const BOOST_METER_CIRCUMFERENCE = 2 * Math.PI * BOOST_METER_RADIUS;
const MATCH_ROSTER_RELEASE_HOLD_MS = 100;
const MATCH_ROSTER_FADE_OUT_MS = 100;

let matchRosterDismissTimer: number | null = null;
let matchRosterFadeTimer: number | null = null;

const app = document.querySelector<HTMLElement>('#app');
if (!app) throw new Error('Missing #app root.');

let session: Readonly<{ start(): Promise<void>; dispose(): void; interaction?: SessionInteraction }> | null = null;
let pendingRoom: RoomClient | null = null;
let onlineHud: OnlineMatchHud | null = null;
let sessionMenu: SessionMenu | null = null;
let rankedRecoveryPrompt: RankedRecoveryPrompt | null = null;
const navigationGuard = new SessionNavigationGuard();
let disposeMenuChat: (() => void) | null = null;
let disposeMusicView: (() => void) | null = null;
let disposeInventoryView: (() => void) | null = null;
let disposeMatchmakingView: (() => void) | null = null;
let disposeAdministrationView: (() => void) | null = null;
let disposeUserSettingsView: (() => void) | null = null;
let disposeLegalNoticesView: (() => void) | null = null;
type HomeScene = Readonly<{
  start(): Promise<void>;
  attach(host: HTMLElement): void;
  whenFirstFrame(): Promise<void>;
  suspend(): void;
  dispose(): void;
}>;

let homeScene: HomeScene | null = null;
let homeSceneInitialization: Promise<HomeScene> | null = null;
let homeSceneViewPreparation: Readonly<{ revision: number; host: HTMLElement; promise: Promise<boolean> }> | null = null;
let viewRevision = 0;
let viewLifetime = new AbortController();
let matchSetupState: MatchSetupState | null = null;
let matchSetupSelectedSource: MatchSetupSource | null = null;
let matchSetupDragSource: MatchSetupSource | null = null;
let matchSetupNotice: string | null = null;
let botCatalogLoad: Promise<void> | null = null;
let botCatalogError: Error | null = null;
const audioRuntime = new AudioRuntime();
const musicSelections = new HttpMusicSelectionSource(`${(import.meta.env.VITE_APP_SERVER_URL as string | undefined)?.trim() ?? ''}/api/music/selections`);
const identity = new BrowserIdentityService();
const inventoryApi = new InventoryApiClient(identity.apiClient);
const loadingScreen = (() => {
  try { return new LoadingScreen(identity.apiClient); }
  catch { return null; }
})();
let privateAccountId: string | null = null;
const inputBindings = new InputBindingStore();
const cameraSettings = new CameraSettingsStore();

const syncPrivateIdentity = async (): Promise<void> => {
  const current = identity.snapshot;
  const accountId = current.kind === 'authenticated' ? current.profile.id : null;
  const previous = privateAccountId;
  privateAccountId = accountId;
  if (previous && previous !== accountId) {
    try { await gameAssetResources.purgePrivateScope(previous); }
    catch { console.warn('Private asset cleanup could not finish.'); }
  }
};

type MatchSetupSource =
  | Readonly<{ kind: 'palette'; botId: string }>
  | Readonly<{ kind: 'local' }>
  | Readonly<{ kind: 'slot'; team: Team; slotIndex: number }>;

const stopSession = (): void => {
  navigationGuard.dispose();
  sessionMenu?.dispose();
  sessionMenu = null;
  session?.dispose();
  session = null;
};

const beginView = (): number => {
  viewLifetime.abort();
  viewLifetime = new AbortController();
  void syncPrivateIdentity();
  disposeMatchmakingView?.();
  disposeMatchmakingView = null;
  onlineHud?.dispose();
  onlineHud = null;
  disposeMusicView?.();
  disposeMusicView = null;
  disposeInventoryView?.();
  disposeInventoryView = null;
  disposeAdministrationView?.();
  disposeAdministrationView = null;
  disposeUserSettingsView?.();
  disposeUserSettingsView = null;
  disposeLegalNoticesView?.();
  disposeLegalNoticesView = null;
  stopSession();
  rankedRecoveryPrompt?.dispose();
  rankedRecoveryPrompt = null;
  pendingRoom?.dispose();
  pendingRoom = null;
  disposeMenuChat?.();
  disposeMenuChat = null;
  homeScene?.suspend();
  return ++viewRevision;
};

const homeButton = (id: string, label: string, primary = false): string =>
  `<button class="home-menu-button${primary ? ' home-menu-button--primary' : ''}" id="${id}" type="button"><span>${escapeHtml(label)}</span><span class="home-menu-button__arrow" aria-hidden="true">›</span></button>`;

const startHomeScene = (revision: number): Promise<boolean> => {
  const host = document.querySelector<HTMLElement>('#home-scene');
  if (!host || typeof host.append !== 'function') return Promise.resolve(true);
  if (homeSceneViewPreparation?.revision === revision && homeSceneViewPreparation.host === host) {
    return homeSceneViewPreparation.promise;
  }
  if (homeScene) {
    homeScene.attach(host);
    const promise = homeScene.whenFirstFrame().then(() => revision === viewRevision);
    homeSceneViewPreparation = { revision, host, promise };
    return promise;
  }
  const promise = (async (): Promise<boolean> => {
    try {
      const scene = await ensureHomeScene();
      if (revision !== viewRevision) return false;
      scene.attach(host);
      await scene.whenFirstFrame();
      return revision === viewRevision;
    } catch (error) {
      if (revision === viewRevision) {
        homeScene = null;
        console.warn('Home scene could not be initialized.', error);
      }
      return false;
    }
  })();
  homeSceneViewPreparation = { revision, host, promise };
  return promise;
};

const ensureHomeScene = async (): Promise<HomeScene> => {
  if (homeScene) return homeScene;
  if (homeSceneInitialization) return homeSceneInitialization;

  const initialization = (async () => {
    const { HomeSceneRenderer } = await import('./game/render/home/HomeSceneRenderer');
    const scene = new HomeSceneRenderer();
    try {
      await scene.start();
      homeScene = scene;
      return scene;
    } catch (error) {
      scene.dispose();
      throw error;
    }
  })();
  homeSceneInitialization = initialization;
  try {
    return await initialization;
  } finally {
    if (homeSceneInitialization === initialization) homeSceneInitialization = null;
  }
};

const enableHomeKeyboardNavigation = (): void => {
  const buttons = Array.from(document.querySelectorAll<HTMLButtonElement>('.home-menu-button'));
  const actions = document.querySelector<HTMLElement>('.home-actions');
  const firstButton = buttons[0];
  if (!actions || !firstButton) return;

  firstButton.focus({ preventScroll: true });
  actions.addEventListener('keydown', (event) => {
    const key = event.key.toLowerCase();
    const direction = key === 'arrowdown' || key === 's' ? 1 : key === 'arrowup' || key === 'w' ? -1 : 0;
    if (direction === 0) return;
    event.preventDefault();
    const activeIndex = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const currentIndex = activeIndex >= 0 ? activeIndex : 0;
    const nextIndex = (currentIndex + direction + buttons.length) % buttons.length;
    buttons[nextIndex]?.focus({ preventScroll: true });
  });
};

const renderHome = (): void => {
  const revision = beginView();
  const activeIdentity = identity.snapshot;
  if (activeIdentity.kind === 'authenticated' && !activeIdentity.profile.onboardingComplete) {
    renderOnboarding();
    return;
  }
  const signedIn = activeIdentity.kind === 'authenticated';
  const accountLabel = signedIn ? activeIdentity.profile.displayName ?? t('menu:home.completeProfile') : t('menu:home.signIn');
  const identityCaption = signedIn
    ? escapeHtml(t('menu:home.signedInAs', { name: accountLabel }))
    : escapeHtml(t('menu:home.guestAs', { name: identity.displayName }));
  app.innerHTML = `
    <main class="shell home-screen">
      <div id="home-scene" class="home-scene" aria-hidden="true"></div>
      <div class="home-screen__wash" aria-hidden="true"></div>
      <section class="home-menu" aria-label="${escapeHtml(t('menu:home.ariaLabel'))}">
        <header class="home-brand">
          <span class="home-brand__sigil" aria-hidden="true">✦</span>
          <h1>${escapeHtml(t('menu:home.brand'))}</h1>
          <span class="home-brand__line" aria-hidden="true"></span>
        </header>
        <nav class="home-actions" aria-label="${escapeHtml(t('menu:home.actionsAriaLabel'))}">
          ${homeButton('play', t('menu:home.play'), true)}
          ${homeButton('settings', t('menu:home.settings'))}
          ${signedIn ? '' : homeButton('account', accountLabel)}
          ${signedIn ? homeButton('music-library', t('menu:home.replayMusic')) : ''}
          ${signedIn ? homeButton('inventory', t('menu:home.inventory')) : ''}
          ${signedIn && activeIdentity.profile.role === 'admin' ? homeButton('administration', t('menu:home.administration')) : ''}
          ${homeButton('legal', t('menu:home.licenses'))}
        </nav>
        <p class="home-account-status">${identityCaption}</p>
        <p id="account-error" class="home-account-error" hidden></p>
      </section>
      <div class="home-input-hint" aria-hidden="true"><kbd>${escapeHtml(t('menu:home.navigateKey'))}</kbd><span>${escapeHtml(t('menu:home.navigate'))}</span><kbd>${escapeHtml(t('menu:home.selectKey'))}</kbd><span>${escapeHtml(t('menu:home.select'))}</span></div>
    </main>
  `;

  void startHomeScene(revision);
  enableHomeKeyboardNavigation();
  if (typeof document.createElement === 'function') disposeMenuChat = mountGlobalChat(app, identity, audioRuntime);
  document.querySelector('#administration')?.addEventListener('click', () => { void renderAdministration(); });
  document.querySelector('#music-library')?.addEventListener('click', () => { void renderMusicLibrary(); });
  document.querySelector('#inventory')?.addEventListener('click', () => { void renderInventory(); });
  document.querySelector('#play')?.addEventListener('click', renderMatchSetup);
  document.querySelector('#settings')?.addEventListener('click', () => { void renderUserSettings(); });
  document.querySelector('#legal')?.addEventListener('click', () => { void renderLegalNotices(); });
  document.querySelector('#account')?.addEventListener('click', () => { void handleGoogleLogin(); });
  if (signedIn) void showRankedRecovery(revision, activeIdentity);
};

const showRankedRecovery = async (revision: number, account: typeof identity.snapshot): Promise<void> => {
  try {
    const recovery = await identity.fetchRankedRecovery();
    if (revision !== viewRevision || identity.snapshot !== account) return;
    const status = document.querySelector<HTMLElement>('#account-error');
    if (status) { status.textContent = ''; status.hidden = true; }
    if (!recovery) return;
    rankedRecoveryPrompt?.dispose();
    rankedRecoveryPrompt = new RankedRecoveryPrompt(app!, recovery, {
      onAccept: async () => {
        const authorization = await identity.acceptRankedRecovery(recovery.matchId);
        if (revision !== viewRevision || identity.snapshot !== account) return;
        await startGame({ mode: 'exhibition', teamSize: recovery.playlist.format, localTeam: null, bots: [] },
          undefined, undefined, authorization);
      },
      onDecline: async () => {
        await identity.declineRankedRecovery(recovery.matchId);
        if (revision === viewRevision && identity.snapshot === account) {
          rankedRecoveryPrompt?.dispose();
          rankedRecoveryPrompt = null;
        }
      },
      revalidate: () => identity.fetchRankedRecovery(),
    });
  } catch (error) {
    if (revision !== viewRevision || identity.snapshot !== account) return;
    const status = document.querySelector<HTMLElement>('#account-error');
    if (status) {
      status.textContent = localizedErrorMessage(error);
      status.hidden = false;
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.textContent = t('common:actions.retry');
      retry.addEventListener('click', () => { void showRankedRecovery(revision, account); });
      status.append(retry);
    }
  }
};

const renderLegalNotices = async (): Promise<void> => {
  const revision = beginView();
  try {
    const { LegalNoticesView } = await import('./legal/LegalNoticesView');
    if (revision !== viewRevision) return;
    const view = new LegalNoticesView({ onBack: renderHome });
    disposeLegalNoticesView = () => view.dispose();
    app.replaceChildren(view.element);
  } catch (error) {
    if (revision !== viewRevision) return;
    renderHome();
    showAccountError(localizedErrorMessage(error));
  }
};

const handleGoogleLogin = async (): Promise<void> => {
  const revision = viewRevision;
  const element = document.querySelector<HTMLButtonElement>('#account');
  const errorElement = document.querySelector<HTMLElement>('#account-error');
  if (!identity.loginConfigured) {
    showAccountError(t('menu:account.loginUnavailable'));
    return;
  }
  if (element) element.disabled = true;
  if (errorElement) errorElement.hidden = true;
  try {
    await identity.signInWithGoogle();
    if (revision === viewRevision) renderHome();
  } catch (error) {
    if (revision !== viewRevision) return;
    showAccountError(localizedErrorMessage(error));
    if (element) element.disabled = false;
  }
};

const renderOnboarding = (): void => {
  const activeIdentity = identity.snapshot;
  if (activeIdentity.kind !== 'authenticated') {
    renderHome();
    return;
  }
  beginView();
  app.innerHTML = `
    <main class="shell sub-screen onboarding-screen">
      <section class="onboarding-card">
        <span class="eyebrow">${escapeHtml(t('menu:onboarding.eyebrow'))}</span>
        <h2>${escapeHtml(t('menu:onboarding.title'))}</h2>
        <p>${escapeHtml(t('menu:onboarding.description'))}</p>
        <form id="onboarding-form" class="profile-form">
          <label>${escapeHtml(t('menu:onboarding.firstName'))}<input name="firstName" autocomplete="given-name" maxlength="64" required /></label>
          <label>${escapeHtml(t('menu:onboarding.lastName'))}<input name="lastName" autocomplete="family-name" maxlength="64" required /></label>
          <label>${escapeHtml(t('menu:onboarding.displayName'))}<input name="displayName" autocomplete="nickname" minlength="3" maxlength="24" required /></label>
          <button class="menu-button profile-form__submit" type="submit"><span>${escapeHtml(t('menu:onboarding.submit'))}</span><span class="menu-button__arrow">›</span></button>
          <p id="onboarding-error" class="account-error" hidden></p>
        </form>
      </section>
    </main>
  `;

  document.querySelector<HTMLFormElement>('#onboarding-form')?.addEventListener('submit', (event) => {
    event.preventDefault();
    void submitOnboarding(event.currentTarget as HTMLFormElement);
  });
};

const submitOnboarding = async (form: HTMLFormElement): Promise<void> => {
  const revision = viewRevision;
  const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');
  if (submit?.disabled) return;
  const errorElement = document.querySelector<HTMLElement>('#onboarding-error');
  if (submit) submit.disabled = true;
  if (errorElement) errorElement.hidden = true;
  const data = new FormData(form);
  try {
    await identity.completeOnboarding({
      firstName: String(data.get('firstName') ?? ''),
      lastName: String(data.get('lastName') ?? ''),
      displayName: String(data.get('displayName') ?? ''),
    });
    if (revision === viewRevision) renderHome();
  } catch (error) {
    if (revision !== viewRevision) return;
    if (errorElement) {
      errorElement.textContent = localizedErrorMessage(error);
      errorElement.hidden = false;
    }
    if (submit) submit.disabled = false;
  }
};

const showAccountError = (message: string): void => {
  const element = document.querySelector<HTMLElement>('#account-error');
  if (!element) return;
  element.textContent = message;
  element.hidden = false;
};

const escapeHtml = (value: string): string => value.replace(/[&<>'"]/g, (character) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
})[character] ?? character);

const I18N_MARKUP_TOKEN = '__I18N_MARKUP_SLOT__';

const translatedMarkup = (message: string, markup: string): string =>
  escapeHtml(message).replace(I18N_MARKUP_TOKEN, markup);

const ensureBotCatalog = (): Promise<void> => {
  if (botCatalogLoad) return botCatalogLoad;
  botCatalogLoad = fetchBotCatalogSnapshot().then((snapshot) => {
    installBotCatalogSnapshot(snapshot);
    reconcileMatchSetupStateWithCatalog();
    botCatalogError = null;
  }).catch((error: unknown) => {
    installBotCatalogSnapshot({ version: 1, revision: 'unavailable', bots: [] });
    reconcileMatchSetupStateWithCatalog();
    botCatalogError = error instanceof Error ? error : new Error(String(error));
  }).finally(() => {
    botCatalogLoad = null;
  });
  return botCatalogLoad;
};

const retryBotCatalog = (): void => {
  if (botCatalogLoad) return;
  botCatalogError = null;
  renderMatchSetup();
};

const reconcileMatchSetupStateWithCatalog = (): void => {
  const state = matchSetupState;
  if (!state) return;
  const catalog = configuredBotCatalog();
  const available = new Set(catalog.filter((bot) => bot.availability === 'available').map((bot) => bot.id));
  const stale = (['blue', 'orange'] as const).some((team) => state.teams[team]
    .some((participant) => participant?.kind === 'bot' && !available.has(participant.botId)));
  if (!stale) return;
  const defaultBot = catalog.find((bot) => bot.id === 'sparring' && bot.availability === 'available')
    ?? catalog.find((bot) => bot.availability === 'available');
  const replacement = createMatchSetupState(defaultBot?.id);
  matchSetupState = state.mode === 'freeplay' ? setMatchSetupMode(replacement, 'freeplay') : replacement;
  matchSetupSelectedSource = null;
  matchSetupDragSource = null;
};

const renderMatchSetup = (): void => {
  const revision = beginView();
  app.innerHTML = `<main class="shell match-setup-screen"><p role="status">${escapeHtml(t('common:status.loading'))}</p></main>`;
  void ensureBotCatalog().then(() => {
    if (revision === viewRevision) renderMatchSetupContent(revision);
  });
};

const renderMatchSetupContent = (revision: number): void => {
  const state = ensureMatchSetupState();
  matchSetupSelectedSource = null;
  matchSetupDragSource = null;
  matchSetupNotice = botCatalogError ? t('match:setup.status.botCatalogUnavailable') : null;
  app.innerHTML = `
    <main class="shell match-setup-screen">
      <div id="home-scene" class="home-scene match-setup-scene" aria-hidden="true"></div>
      <div class="match-setup-screen__wash" aria-hidden="true"></div>
      <header class="match-setup-header">
        <div class="match-setup-header__identity">
          <button class="match-setup-back" id="back" type="button" aria-label="${escapeHtml(t('match:setup.backToMainMenu'))}">←</button>
          <div>
            <span class="match-setup-kicker">${escapeHtml(t('match:setup.eyebrow'))}</span>
            <h1>${escapeHtml(t('match:setup.title'))}</h1>
            <p>${escapeHtml(t('match:setup.description'))}</p>
          </div>
        </div>
        <div class="match-format-picker" role="group" aria-label="${escapeHtml(t('match:setup.modeAriaLabel'))}">
          <button id="match-setup-local" class="match-format-button${state.mode === 'exhibition' ? ' is-active' : ''}" type="button">${escapeHtml(t('match:setup.localMatch'))}</button>
          <button id="match-setup-online" class="match-format-button" type="button">${escapeHtml(t('match:setup.onlineMatch'))}</button>
          <button id="match-setup-freeplay" class="match-format-button${state.mode === 'freeplay' ? ' is-active' : ''}" type="button">${escapeHtml(t('match:setup.freeplay'))}</button>
        </div>
      </header>
      <section id="match-setup-board" class="match-setup-board" aria-live="polite">
        ${matchSetupBoardMarkup(state)}
      </section>
      <section id="match-setup-bots" class="match-bot-dock${state.mode === 'freeplay' ? ' is-hidden' : ''}" aria-label="${escapeHtml(t('match:setup.availableBotsAriaLabel'))}">
        ${matchSetupBotDockMarkup(state)}
      </section>
      <footer class="match-setup-footer">
        <p id="match-setup-status" class="match-setup-status">${escapeHtml(matchSetupStatus(state))}</p>
        <button id="match-setup-start" class="match-setup-start" type="button" ${matchSetupCanStart(state) ? '' : 'disabled'}>
          ${matchSetupStartLabel(state)} <span aria-hidden="true">›</span>
        </button>
      </footer>
    </main>
  `;

  void startHomeScene(revision);
  bindMatchSetupStaticInteractions();
  bindMatchSetupDynamicInteractions();
};

const renderMatchmaking = (initialPlaylist?: Pick<QueueTicket['search'], 'mode' | 'format'>,
  failedReservationId: string | null = null): void => {
  const revision = beginView();
  const view = new MatchmakingView(identity, {
    onBack: renderHome,
    onLocal: renderMatchSetup,
    onFreeplay: () => {
      matchSetupState = setMatchSetupMode(ensureMatchSetupState(), 'freeplay');
      renderMatchSetup();
    },
    onReserved: (ticket) => {
      if (disposeMatchmakingView === null || !ticket.playlist || !ticket.connection) return;
      void startGame({ mode: 'exhibition', teamSize: ticket.playlist.format, localTeam: null, bots: [] }, undefined, ticket);
    },
  }, initialPlaylist, failedReservationId);
  disposeMatchmakingView = () => view.dispose();
  app.replaceChildren(view.element);
  void startHomeScene(revision);
};

const renderMusicLibrary = async (orderId?: string): Promise<void> => {
  const current = identity.snapshot;
  if (current.kind !== 'authenticated') { renderHome(); return; }
  const revision = beginView();
  app.innerHTML = `<main class="shell sub-screen"><p role="status">${escapeHtml(t('menu:loading.music'))}</p></main>`;
  try {
    const { MusicLibraryView } = await import('./music/replay/MusicLibraryView');
    if (revision !== viewRevision || identity.snapshot !== current) return;
    const view = new MusicLibraryView({ api: identity.apiClient, audioRuntime, profile: current.profile,
      onBack: renderHome, orderId });
    disposeMusicView = () => view.dispose();
    app.replaceChildren(view.element);
  } catch (error) {
    if (revision !== viewRevision) return;
    renderHome();
    showAccountError(localizedErrorMessage(error));
  }
};

const renderInventory = async (orderId?: string): Promise<void> => {
  const current = identity.snapshot;
  if (current.kind !== 'authenticated') { renderHome(); return; }
  const revision = beginView();
  app.innerHTML = `<main class="shell sub-screen"><p role="status">${escapeHtml(t('common:status.loading'))}</p></main>`;
  try {
    const { InventoryView } = await import('./inventory/InventoryView');
    if (revision !== viewRevision || identity.snapshot !== current) return;
    const view = new InventoryView({ api: inventoryApi, orderId, onBack: renderHome,
      isCurrent: () => revision === viewRevision && identity.snapshot === current });
    disposeInventoryView = () => view.dispose();
    app.replaceChildren(view.element);
  } catch (error) {
    if (revision !== viewRevision) return;
    renderHome();
    showAccountError(localizedErrorMessage(error));
  }
};

const renderUserSettings = async (): Promise<void> => {
  const revision = beginView();
  app.innerHTML = `
    <main class="shell home-screen">
      <div id="home-scene" class="home-scene" aria-hidden="true"></div>
      <div class="home-screen__wash" aria-hidden="true"></div>
      <section class="home-menu" aria-live="polite"><p class="home-account-status">${escapeHtml(t('menu:loading.settings'))}</p></section>
    </main>
  `;
  void startHomeScene(revision);
  try {
    const { UserSettingsView } = await import('./settings/UserSettingsView');
    if (revision !== viewRevision) return;
    const view = new UserSettingsView({
      identity,
      inputBindings,
      cameraSettings,
      onBack: renderHome,
      onSignedOut: () => { void syncPrivateIdentity().then(renderHome); },
      onLocaleChanged: () => { void renderUserSettings(); },
    });
    disposeUserSettingsView = () => view.dispose();
    app.replaceChildren(view.element);
    void startHomeScene(revision);
  } catch (error) {
    if (revision !== viewRevision) return;
    renderHome();
    showAccountError(localizedErrorMessage(error));
  }
};

const renderAdministration = async (): Promise<void> => {
  const current = identity.snapshot;
  if (current.kind !== 'authenticated' || current.profile.role !== 'admin') { renderHome(); return; }
  const revision = beginView();
  app.innerHTML = `
    <main class="shell home-screen">
      <div id="home-scene" class="home-scene" aria-hidden="true"></div>
      <div class="home-screen__wash" aria-hidden="true"></div>
      <section class="home-menu" aria-live="polite"><p class="home-account-status">${escapeHtml(t('menu:loading.administration'))}</p></section>
    </main>
  `;
  void startHomeScene(revision);
  try {
    const { AdministrationView } = await import('./administration/AdministrationView');
    if (revision !== viewRevision || identity.snapshot !== current) return;
    const view = new AdministrationView({ identity, audioRuntime, onBack: renderHome });
    disposeAdministrationView = () => view.dispose();
    app.replaceChildren(view.element);
    void startHomeScene(revision);
  } catch (error) {
    if (revision !== viewRevision) return;
    renderHome();
    showAccountError(localizedErrorMessage(error));
  }
};

const ensureMatchSetupState = (): MatchSetupState => {
  if (matchSetupState) return matchSetupState;
  const bots = configuredBotCatalog().filter((bot) => bot.availability === 'available');
  const defaultBot = bots.find((bot) => bot.id === 'sparring') ?? bots[0];
  matchSetupState = createMatchSetupState(defaultBot?.id);
  return matchSetupState;
};

const matchSetupBoardMarkup = (state: MatchSetupState): string => {
  if (state.mode === 'freeplay') {
    return `
      <section class="match-freeplay-card">
        <div class="match-freeplay-card__sigil" aria-hidden="true">✦</div>
        <span>${escapeHtml(t('match:setup.freeplayCard.arena'))}</span>
        <h2>${escapeHtml(t('match:setup.freeplayCard.title'))}</h2>
        <p>${escapeHtml(t('match:setup.freeplayCard.description'))}</p>
        <div class="match-freeplay-card__features"><span>${escapeHtml(t('match:setup.freeplayCard.noOpponents'))}</span><span>${escapeHtml(t('match:setup.freeplayCard.unlimitedBoost'))}</span></div>
      </section>
    `;
  }

  const blueCount = state.teams.blue.filter(Boolean).length;
  const orangeCount = state.teams.orange.filter(Boolean).length;

  return `
    ${matchTeamMarkup('blue', state)}
    <div class="match-setup-versus" aria-hidden="true">
      <span>${formatNumber(blueCount)} × ${formatNumber(orangeCount)}</span>
      <strong>${escapeHtml(t('match:setup.versus'))}</strong>
      <i></i>
    </div>
    ${matchTeamMarkup('orange', state)}
  `;
};

const matchTeamMarkup = (team: Team, state: MatchSetupState): string => {
  const label = team === 'blue' ? t('match:setup.team.blue') : t('match:setup.team.orange');
  const occupied = state.teams[team].filter(Boolean).length;
  const slots = state.teams[team].map((participant, slotIndex) => {
    const selected = matchSetupSelectedSource?.kind === 'slot'
      && matchSetupSelectedSource.team === team
      && matchSetupSelectedSource.slotIndex === slotIndex;
    if (!participant) {
      return `
        <div class="match-team-slot match-team-slot--empty" data-setup-team="${team}" data-setup-slot="${slotIndex}" tabindex="0" role="button" aria-label="${escapeHtml(t('match:setup.team.emptySlotAria', { team: label }))}">
          <span class="match-team-slot__plus" aria-hidden="true">+</span>
          <strong>${escapeHtml(t('match:setup.team.emptySlotAction'))}</strong>
        </div>
      `;
    }
    if (participant.kind === 'local') {
      return `
        <div class="match-team-slot match-team-slot--player${selected ? ' is-selected' : ''}" data-setup-team="${team}" data-setup-slot="${slotIndex}" data-setup-occupied="true" draggable="true" tabindex="0" role="button">
          <span class="match-team-slot__avatar" aria-hidden="true">★</span>
          <div><strong>${escapeHtml(identity.displayName)}</strong><span>${escapeHtml(t('match:setup.team.you'))}</span></div>
          <span class="match-team-slot__grip" aria-hidden="true">⋮⋮</span>
        </div>
      `;
    }
    const bot = botDefinition(participant.botId);
    return `
      <div class="match-team-slot match-team-slot--bot${selected ? ' is-selected' : ''}" data-setup-team="${team}" data-setup-slot="${slotIndex}" data-setup-occupied="true" draggable="true" tabindex="0" role="button">
        <span class="match-team-slot__bot-mark" aria-hidden="true">◇</span>
        <div><strong>${escapeHtml(bot.name)}</strong><span>${escapeHtml(bot.approximateRank ?? t('match:setup.team.unranked'))}</span></div>
        <span class="match-team-slot__grip" aria-hidden="true">⋮⋮</span>
      </div>
    `;
  }).join('');
  return `
    <section class="match-team match-team--${team}" data-team="${team}">
      <header class="match-team__header">
        <div><span class="match-team__dot" aria-hidden="true"></span><div><small>${escapeHtml(t('match:setup.team.label'))}</small><h2>${escapeHtml(label)}</h2></div></div>
        <strong>${formatNumber(occupied)}/${formatNumber(MATCH_SETUP_TEAM_CAPACITY)}</strong>
      </header>
      <div class="match-team__slots">${slots}</div>
    </section>
  `;
};

const matchSetupBotDockMarkup = (state: MatchSetupState): string => {
  const bots = botAvailableForMatchSetup(state) ? configuredBotCatalog() : [];
  const localCard = state.mode === 'exhibition' && matchSetupLocalTeam(state) === null ? `
    <button class="match-bot-card match-bot-card--local${matchSetupSelectedSource?.kind === 'local' ? ' is-selected' : ''}" type="button" data-setup-local="true" draggable="true">
      <strong>${escapeHtml(identity.displayName)}</strong>
      <span>${escapeHtml(t('match:setup.team.youSpectator'))}</span>
      <small>${escapeHtml(t('match:setup.team.returnAction'))}</small>
    </button>
  ` : '';
  const cards = bots.map((bot) => {
    const selected = matchSetupSelectedSource?.kind === 'palette' && matchSetupSelectedSource.botId === bot.id;
    const comingSoon = bot.availability === 'coming-soon';
    return `
      <button class="match-bot-card${selected ? ' is-selected' : ''}" type="button" ${comingSoon ? 'disabled' : `data-setup-bot="${escapeHtml(bot.id)}" draggable="true"`}>
        <strong>${escapeHtml(bot.name)}</strong>
        <span>${escapeHtml(bot.approximateRank ?? t('match:setup.team.unranked'))}</span>
        <small>${escapeHtml(comingSoon ? t('match:setup.botDock.comingSoon') : botTrainingFormatLabel(bot.policy.trainingFormat))}</small>
      </button>
    `;
  }).join('');
  const retry = state.mode === 'exhibition' && botCatalogError ? `
    <button class="match-bot-card match-bot-card--retry" id="match-setup-retry-bots" type="button">
      <strong>${escapeHtml(t('common:actions.retry'))}</strong>
      <span>${escapeHtml(t('match:setup.botDock.unavailable'))}</span>
    </button>
  ` : '';
  return `
    <div class="match-bot-dock__heading"><span>${escapeHtml(t('match:setup.botDock.available'))}</span><small>${escapeHtml(t('match:setup.botDock.addAction'))}</small></div>
    <div class="match-bot-list">${localCard}${cards}${retry || (!cards ? `<span class="match-bot-list__empty">${escapeHtml(t('match:setup.botDock.empty'))}</span>` : '')}</div>
  `;
};

const botTrainingFormatLabel = (format: ReturnType<typeof botDefinition>['policy']['trainingFormat']): string => {
  if (format === 'multi-player') return t('match:setup.botDock.trainedMulti');
  if (format === 'not-applicable') return t('match:setup.botDock.heuristic');
  return t('match:setup.botDock.trained', { format });
};

const matchSetupStartLabel = (state: MatchSetupState): string => {
  if (state.mode === 'freeplay') return t('match:setup.start.freeplay');
  return matchSetupLocalTeam(state) === null ? t('match:setup.start.spectate') : t('match:setup.start.match');
};

const matchSetupStatus = (state: MatchSetupState): string => {
  if (matchSetupNotice) return matchSetupNotice;
  if (state.mode === 'freeplay') return t('match:setup.status.freeplayReady');
  const blueCount = state.teams.blue.filter(Boolean).length;
  const orangeCount = state.teams.orange.filter(Boolean).length;
  const teamSize = matchSetupTeamSize(state);
  if (teamSize) {
    return matchSetupLocalTeam(state) === null
      ? t('match:setup.status.spectatorReady', { teamSize: formatNumber(teamSize) })
      : t('match:setup.status.matchReady', { teamSize: formatNumber(teamSize) });
  }
  if (blueCount === 0 || orangeCount === 0) return t('match:setup.status.teamNeedsParticipant');
  const blue = t('match:setup.status.participantCount', { count: blueCount, formattedCount: formatNumber(blueCount) });
  const orange = t('match:setup.status.participantCount', { count: orangeCount, formattedCount: formatNumber(orangeCount) });
  return t('match:setup.status.balanceTeams', { blue, orange });
};

const matchSetupCanStart = (state: MatchSetupState): boolean =>
  matchSetupReady(state) && (state.mode === 'freeplay' || (botCatalogError === null
    && (['blue', 'orange'] as const).every(team => state.teams[team].every(participant =>
      participant?.kind !== 'bot' || configuredBotCatalog().some(bot => bot.id === participant.botId && bot.availability === 'available')))));

const bindMatchSetupStaticInteractions = (): void => {
  document.querySelector('#back')?.addEventListener('click', renderHome);
  document.querySelector('#match-setup-local')?.addEventListener('click', () => {
    matchSetupState = setMatchSetupMode(ensureMatchSetupState(), 'exhibition');
    matchSetupSelectedSource = null;
    matchSetupNotice = botCatalogError ? t('match:setup.status.botCatalogUnavailable') : null;
    updateMatchSetupUi();
  });
  document.querySelector('#match-setup-online')?.addEventListener('click', () => renderMatchmaking());
  document.querySelector('#match-setup-freeplay')?.addEventListener('click', () => {
    matchSetupState = setMatchSetupMode(ensureMatchSetupState(), 'freeplay');
    matchSetupSelectedSource = null;
    matchSetupNotice = null;
    updateMatchSetupUi();
  });
  document.querySelector('#match-setup-start')?.addEventListener('click', () => {
    const state = ensureMatchSetupState();
    if (!matchSetupCanStart(state)) return;
    try {
      const config = matchConfigFromSetup(state);
      void startGame(config);
    } catch (error) {
      matchSetupNotice = localizedErrorMessage(error);
      updateMatchSetupUi();
    }
  });
  const dock = document.querySelector<HTMLElement>('#match-setup-bots');
  dock?.addEventListener('dragover', handleMatchSetupDockDragOver);
  dock?.addEventListener('drop', handleMatchSetupDockDrop);
};

const updateMatchSetupUi = (): void => {
  const state = ensureMatchSetupState();
  const board = document.querySelector<HTMLElement>('#match-setup-board');
  const bots = document.querySelector<HTMLElement>('#match-setup-bots');
  const status = document.querySelector<HTMLElement>('#match-setup-status');
  const start = document.querySelector<HTMLButtonElement>('#match-setup-start');
  if (board) board.innerHTML = matchSetupBoardMarkup(state);
  if (bots) {
    bots.innerHTML = matchSetupBotDockMarkup(state);
    bots.className = `match-bot-dock${state.mode === 'freeplay' ? ' is-hidden' : ''}`;
  }
  if (status) status.textContent = matchSetupStatus(state);
  if (start) {
    start.disabled = !matchSetupCanStart(state);
    start.textContent = `${matchSetupStartLabel(state)} ›`;
  }
  const local = document.querySelector<HTMLElement>('#match-setup-local');
  if (local) local.className = `match-format-button${state.mode === 'exhibition' ? ' is-active' : ''}`;
  const freeplay = document.querySelector<HTMLElement>('#match-setup-freeplay');
  if (freeplay) freeplay.className = `match-format-button${state.mode === 'freeplay' ? ' is-active' : ''}`;
  bindMatchSetupDynamicInteractions();
};

const bindMatchSetupDynamicInteractions = (): void => {
  document.querySelector('#match-setup-retry-bots')?.addEventListener('click', retryBotCatalog);
  document.querySelectorAll<HTMLElement>('[data-setup-slot]').forEach((slot) => {
    const team = slot.dataset.setupTeam as Team | undefined;
    const slotIndex = Number(slot.dataset.setupSlot);
    if (!team || !Number.isSafeInteger(slotIndex)) return;
    slot.addEventListener('click', () => handleMatchSetupSlotActivation(team, slotIndex));
    slot.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      handleMatchSetupSlotActivation(team, slotIndex);
    });
    if (slot.dataset.setupOccupied === 'true') {
      slot.addEventListener('dragstart', (event) => {
        matchSetupDragSource = { kind: 'slot', team, slotIndex };
        if (event.dataTransfer) {
          event.dataTransfer.effectAllowed = 'move';
          event.dataTransfer.setData('text/plain', 'rocket-soccer-participant');
        }
      });
      slot.addEventListener('dragend', () => { matchSetupDragSource = null; });
    }
    slot.addEventListener('dragover', (event) => {
      if (!matchSetupDragSource) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = matchSetupDragSource.kind === 'palette' ? 'copy' : 'move';
    });
    slot.addEventListener('drop', (event) => {
      event.preventDefault();
      const source = matchSetupDragSource;
      matchSetupDragSource = null;
      if (source) applyMatchSetupSource(source, team, slotIndex);
    });
  });

  document.querySelectorAll<HTMLElement>('[data-setup-bot]').forEach((card) => {
    const botId = card.dataset.setupBot;
    if (!botId) return;
    card.addEventListener('click', () => {
      if (matchSetupSelectedSource?.kind === 'slot') {
        const selected = matchSetupSelectedSource;
        applyMatchSetupSource({ kind: 'palette', botId }, selected.team, selected.slotIndex);
        return;
      }
      matchSetupSelectedSource = matchSetupSelectedSource?.kind === 'palette' && matchSetupSelectedSource.botId === botId
        ? null
        : { kind: 'palette', botId };
      matchSetupNotice = matchSetupSelectedSource ? t('match:setup.notice.botSelected') : null;
      updateMatchSetupUi();
    });
    card.addEventListener('dragstart', (event) => {
      matchSetupDragSource = { kind: 'palette', botId };
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'copy';
        event.dataTransfer.setData('text/plain', 'rocket-soccer-bot');
      }
    });
    card.addEventListener('dragend', () => { matchSetupDragSource = null; });
  });

  const localCard = document.querySelector<HTMLElement>('[data-setup-local]');
  localCard?.addEventListener('click', () => {
    if (matchSetupSelectedSource?.kind === 'slot') {
      const selected = matchSetupSelectedSource;
      applyMatchSetupSource({ kind: 'local' }, selected.team, selected.slotIndex);
      return;
    }
    matchSetupSelectedSource = matchSetupSelectedSource?.kind === 'local' ? null : { kind: 'local' };
    matchSetupNotice = matchSetupSelectedSource ? t('match:setup.notice.youSelected') : null;
    updateMatchSetupUi();
  });
  localCard?.addEventListener('dragstart', (event) => {
    matchSetupDragSource = { kind: 'local' };
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', 'rocket-soccer-local-player');
    }
  });
  localCard?.addEventListener('dragend', () => { matchSetupDragSource = null; });

};

const handleMatchSetupDockDragOver = (event: DragEvent): void => {
  if (matchSetupDragSource?.kind !== 'slot') return;
  const participant = ensureMatchSetupState().teams[matchSetupDragSource.team][matchSetupDragSource.slotIndex];
  if (!participant) return;
  event.preventDefault();
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
};

const handleMatchSetupDockDrop = (event: DragEvent): void => {
  const source = matchSetupDragSource;
  matchSetupDragSource = null;
  if (source?.kind !== 'slot') return;
  const state = ensureMatchSetupState();
  const participant = state.teams[source.team][source.slotIndex];
  if (!participant) return;
  event.preventDefault();
  matchSetupState = removeMatchSetupParticipant(state, source.team, source.slotIndex);
  matchSetupSelectedSource = null;
  matchSetupNotice = participant.kind === 'local'
    ? t('match:setup.notice.spectatorEnabled')
    : t('match:setup.notice.botRemoved');
  updateMatchSetupUi();
};

const handleMatchSetupSlotActivation = (team: Team, slotIndex: number): void => {
  const state = ensureMatchSetupState();
  if (matchSetupSelectedSource) {
    applyMatchSetupSource(matchSetupSelectedSource, team, slotIndex);
    return;
  }
  if (!state.teams[team][slotIndex]) return;
  matchSetupSelectedSource = { kind: 'slot', team, slotIndex };
  matchSetupNotice = t('match:setup.notice.participantSelected');
  updateMatchSetupUi();
};

const applyMatchSetupSource = (source: MatchSetupSource, team: Team, slotIndex: number): void => {
  const state = ensureMatchSetupState();
  let next = state;
  if (source.kind === 'palette') {
    if (!configuredBotCatalog().some(bot => bot.id === source.botId && bot.availability === 'available')) return;
    if (!canPlaceBot(state, team)) {
      matchSetupNotice = t('match:setup.notice.invalidBotTeam');
      updateMatchSetupUi();
      return;
    }
    next = placeBot(state, source.botId, team, slotIndex);
  } else if (source.kind === 'local') {
    next = placeLocalPlayer(state, team, slotIndex);
  } else {
    next = moveMatchSetupParticipant(state, source.team, source.slotIndex, team, slotIndex);
  }
  matchSetupState = next;
  matchSetupSelectedSource = null;
  matchSetupNotice = next === state ? t('match:setup.notice.chooseAnotherSlot') : null;
  updateMatchSetupUi();
};

const startGame = async (config: MatchConfig, spectatorCode?: string, queueTicket?: QueueTicket,
  recovery?: RankedRecoveryAuthorization): Promise<void> => {
  void audioRuntime.unlock().catch(() => undefined);
  const revision = beginView();
  loadingScreen?.show('match');
  loadingScreen?.setProgress(0.08, t('common:loading.preparingMatch'));
  performance.mark?.('app:match-loading:begin');
  const showTeams = config.mode !== 'freeplay';
  const inputHint = (key: 'takePossession' | 'startDribble' | 'passBall' | 'reset', action: keyof typeof inputBindings.bindings): string =>
    translatedMarkup(
      t(`match:training.${key}`, { slot: I18N_MARKUP_TOKEN }),
      `<kbd>${escapeHtml(inputBindingLabel(inputBindings.bindings[action], 'keyboard-mouse'))}</kbd>`,
    );
  const trainingHints = config.mode === 'freeplay'
    ? `<div class="training-hints">
        <span>${inputHint('takePossession', 'training-take-possession')}</span>
        <span>${inputHint('startDribble', 'training-start-dribble')}</span>
        <span>${inputHint('passBall', 'training-pass-ball')}</span>
        <span>${inputHint('reset', 'training-reset')}</span>
      </div>`
    : '';
  const ballCameraHint = escapeHtml(inputBindingLabel(inputBindings.bindings['toggle-ball-camera'], 'keyboard-mouse'));
  const replaySkipHint = escapeHtml(inputBindingLabel(inputBindings.bindings['skip-replay'], 'keyboard-mouse'));
  const cameraToggleHint = translatedMarkup(
    t('match:hud.cameraToggleHint', { slot: I18N_MARKUP_TOKEN }),
    `<kbd id="camera-mode-binding">${ballCameraHint}</kbd>`,
  );
  const replaySkipAction = translatedMarkup(
    t('match:replay.skipAction', { slot: I18N_MARKUP_TOKEN }),
    `<span class="replay-skip__mouse" aria-hidden="true"></span><kbd id="replay-skip-binding">${replaySkipHint}</kbd>`,
  );
  const replayWaiting = translatedMarkup(
    t('match:replay.waitingFor', { slot: I18N_MARKUP_TOKEN }),
    '<strong id="replay-pending"></strong>',
  );
  app.innerHTML = `
    <main class="game-screen">
      <div id="game-host" class="game-host"></div>
      <div class="hud" aria-live="polite">
        ${showTeams ? `<div class="scoreboard"><span id="score-blue" class="score score--blue">${formatNumber(0)}</span><span class="scoreboard__dash">:</span><span id="score-orange" class="score score--orange">${formatNumber(0)}</span></div>` : ''}
        <div class="hud__mode">${escapeHtml(config.mode === 'freeplay' ? t('match:hud.freeplay') : t('match:hud.exhibition', { teamSize: formatNumber(config.teamSize) }))}</div>
      </div>
      <div class="speedometer"><strong id="speed">${formatNumber(0)}</strong><span>${escapeHtml(t('match:hud.speedUnit'))}</span></div>
      <div id="camera-mode-indicator" class="camera-mode-indicator${config.mode === 'freeplay' ? ' camera-mode-indicator--with-training' : ''}" hidden>
        <div class="camera-mode-indicator__title"><span class="camera-mode-indicator__dot"></span>${escapeHtml(t('match:hud.ballCamera'))}</div>
        <div class="camera-mode-indicator__hint">${cameraToggleHint}</div>
      </div>
      <div id="spectator-indicator" class="spectator-indicator" hidden>
        <span>${escapeHtml(t('match:spectator.label'))}</span>
        <strong id="spectator-target"></strong>
        <div><kbd id="spectator-previous">←</kbd><span>${escapeHtml(t('match:spectator.changeCar'))}</span><kbd id="spectator-next">→</kbd></div>
      </div>
      <div id="replay-overlay" class="replay-overlay" hidden>
        <div id="replay-scorer-card" class="replay-scorer-card${showTeams ? '' : ' replay-scorer-card--neutral'}">
          <span class="replay-scorer-card__eyebrow">${escapeHtml(t('match:replay.label'))}</span>
          ${showTeams ? '<strong id="replay-scorer"></strong>' : ''}
          <span id="replay-speed" class="replay-scorer-card__speed"></span>
        </div>
        <div id="replay-skip" class="replay-skip" data-device="keyboard-mouse">
          <div class="replay-skip__action">${replaySkipAction}</div>
          <div class="replay-skip__pending">${replayWaiting}</div>
        </div>
      </div>
      ${showTeams ? `<div id="match-roster-overlay" class="match-roster-overlay" hidden aria-hidden="true">
        <section class="match-roster-panel" aria-label="${escapeHtml(t('match:roster.ariaLabel'))}">
          <div id="match-roster-teams" class="match-roster-teams"></div>
        </section>
      </div>` : ''}
      <div id="boost-meter" class="boost-meter" role="progressbar" aria-label="${escapeHtml(t('match:hud.boost'))}" aria-valuemin="0" aria-valuemax="100" aria-valuenow="100">
        <svg class="boost-meter__progress" viewBox="0 0 100 100" aria-hidden="true">
          <circle class="boost-meter__track" cx="50" cy="50" r="${BOOST_METER_RADIUS}"></circle>
          <circle
            id="boost-progress"
            class="boost-meter__fill"
            cx="50"
            cy="50"
            r="${BOOST_METER_RADIUS}"
            stroke-dasharray="${BOOST_METER_CIRCUMFERENCE}"
            stroke-dashoffset="0"
          ></circle>
        </svg>
        <div class="boost-meter__ring"><strong id="boost">${formatNumber(100)}</strong><span>${escapeHtml(t('match:hud.boost'))}</span></div>
      </div>
      ${trainingHints}
      ${showTeams ? `<div id="goal-banner" class="goal-banner" hidden>${escapeHtml(t('match:hud.goal'))}</div>` : ''}
      <div id="kickoff-countdown" class="kickoff-countdown" hidden></div>
      <div id="game-error" class="game-error" hidden></div>
    </main>
  `;

  const host = document.querySelector<HTMLElement>('#game-host');
  const gameScreen = document.querySelector<HTMLElement>('.game-screen');
  if (!host || !gameScreen) {
    loadingScreen?.hide();
    return;
  }
  loadingScreen?.setProgress(0.18, t('common:loading.preparingMatch'));
  let definition;
  let room: RoomClient | null = null;
  let privateAllocationId: string | null = null;
  let privateAllocationToken: string | null = null;
  let localChatAuthorId: string | null = null;
  try {
    if (!spectatorCode && !queueTicket) loadingScreen?.setProgress(0.26, t('common:loading.preparingMatch'));
    const contentSnapshot = spectatorCode || queueTicket || recovery ? null : await identity.apiClient.getGameContentSnapshot();
    if (revision !== viewRevision) return;
    definition = contentSnapshot ? createMatchDefinition(config, contentSnapshot, identity.displayName) : null;
    if (spectatorCode || queueTicket || recovery || definition?.authority === 'server') {
      if (config.mode !== 'exhibition') throw new Error('Free play does not use online rooms.');
      if (queueTicket && (!queueTicket.connection || !queueTicket.playlist)) {
        throw new Error('Match reservation has no assigned server route.');
      }
      let serverUrl = recovery?.connection.websocketUrl ?? queueTicket?.connection?.websocketUrl;
      if (!serverUrl && spectatorCode) {
        serverUrl = (await identity.apiClient.resolvePrivateMatchRoom(spectatorCode)).connection.websocketUrl;
      } else if (!serverUrl) {
        const onlineSession = await identity.getOnlineSession();
        localChatAuthorId = onlineSession.identity.id;
        privateAllocationToken = onlineSession.token;
        const allocation = await identity.apiClient.allocatePrivateMatchServer(onlineSession.token);
        privateAllocationId = allocation.allocationId;
        serverUrl = allocation.connection.websocketUrl;
      }
      room = new RoomClient(serverUrl, async () => {
        const onlineSession = await identity.getOnlineSession();
        localChatAuthorId = onlineSession.identity.id;
        return onlineSession.token;
      },
        recovery ? { type: 'recover', roomId: recovery.matchId, authorization: recovery.token }
          : queueTicket && queueTicket.roomId && queueTicket.reservationId
          ? { type: 'admit', roomId: queueTicket.roomId, reservationId: queueTicket.reservationId }
          : spectatorCode ? { type: 'join', code: spectatorCode } : {
          type: 'create', allocationId: privateAllocationId ?? undefined, config: { teamSize: config.teamSize, bots: config.bots,
            playerAdmission: 'closed', spectatorAdmission: 'open' },
        });
      pendingRoom = room;
      loadingScreen?.setIndeterminate(t('common:loading.waitingForServer'));
      const welcome = await room.connect();
      if (privateAllocationId && privateAllocationToken) {
        await identity.apiClient.bindPrivateMatchRoom(privateAllocationToken, privateAllocationId, welcome.room.code);
      }
      if (revision !== viewRevision) {
        room.dispose();
        if (privateAllocationId && privateAllocationToken) {
          await identity.apiClient.releasePrivateMatchServer(privateAllocationToken, privateAllocationId).catch(() => undefined);
        }
        return;
      }
      navigationGuard.setActive(welcome.room.phase === 'running');
      definition = buildOnlineMatchDefinition(welcome);
      loadingScreen?.setProgress(0.52, t('common:loading.preparingMatch'));
    } else {
      loadingScreen?.setProgress(0.52, t('common:loading.preparingMatch'));
    }
  } catch (error) {
    room?.dispose();
    if (privateAllocationId && privateAllocationToken) {
      await identity.apiClient.releasePrivateMatchServer(privateAllocationToken, privateAllocationId).catch(() => undefined);
    }
    if (revision === viewRevision) {
      loadingScreen?.hide();
      if (recovery) {
        renderHome();
        const status = document.querySelector<HTMLElement>('#account-error');
        if (status) { status.textContent = localizedErrorMessage(error); status.hidden = false; }
      } else if (queueTicket) {
        renderMatchmaking(queueTicket.search, queueTicket.reservationId);
        const status = document.querySelector<HTMLElement>('.matchmaking-status');
        if (status) status.textContent = localizedErrorMessage(error);
      } else { renderMatchSetup(); matchSetupNotice = localizedErrorMessage(error); updateMatchSetupUi(); }
    }
    return;
  }
  if (revision !== viewRevision) return;
  if (!definition) {
    loadingScreen?.hide();
    return;
  }
  if (!showTeams) gameScreen.style.setProperty('--freeplay-color', `#${definition.teamPresentation.colors.blue.toString(16).padStart(6, '0')}`);
  const explicitExit = async (): Promise<void> => {
    if (room) {
      const participant = room.welcome.room.participants.find(item => item.id === room.welcome.participantId);
      const definitiveRanked = room.welcome.room.phase === 'running'
        && room.welcome.room.settings.mode === 'ranked'
        && participant?.role === 'player'
        && (room.operation.type === 'admit' || room.operation.type === 'recover')
        && identity.snapshot.kind === 'authenticated';
      if (!room.leave()) {
        if (definitiveRanked) {
          await identity.declineRankedRecovery(room.welcome.room.id);
        } else if (room.welcome.room.phase === 'running') {
          throw new Error('Could not leave the match while disconnected. Try again after reconnecting.');
        }
      }
      if (revision !== viewRevision) return;
    }
    if (privateAllocationId && privateAllocationToken) {
      await identity.apiClient.releasePrivateMatchServer(privateAllocationToken, privateAllocationId);
      privateAllocationId = null;
    }
    if (queueTicket || recovery) renderMatchmaking();
    else renderMatchSetup();
  };
  if (room) onlineHud = new OnlineMatchHud(gameScreen, room,
    () => { void explicitExit().catch(error => {
      const element = document.querySelector<HTMLElement>('#game-error');
      if (element) { element.textContent = localizedErrorMessage(error); element.hidden = false; }
    }); },
    queueTicket ? () => renderMatchmaking(queueTicket.search) : renderMatchSetup,
    () => sessionMenu?.refresh());
  let focusedEntityId = definition.localEntityId;
  let matchReady = false;
  const callbacks: GameSessionCallbacks = {
    onReady: () => {
      if (revision !== viewRevision || matchReady) return;
      matchReady = true;
      performance.mark?.('app:match-loading:ready');
      performance.measure?.('app:match-loading-to-ready', 'app:match-loading:begin', 'app:match-loading:ready');
      loadingScreen?.hide();
    },
    onState: (state) => {
      if (revision !== viewRevision) return;
      updateHud(state, focusedEntityId);
      onlineHud?.updateMatchState(state);
      navigationGuard.setActive(state.result === null && (!room || room.welcome.room.phase === 'running'));
      sessionMenu?.refresh();
    },
    onCameraState: updateCameraHud,
    onReplayState: updateReplayHud,
    onRosterState: updateMatchRosterHud,
    onSpectatorState: (state) => {
      focusedEntityId = state.active ? state.targetEntityId : state.controlledEntityId;
      updateSpectatorHud(state);
    },
    onError: (error) => {
      if (!matchReady && revision === viewRevision && loadingScreen) {
        loadingScreen.setError(localizedErrorMessage(error), () => {
          if (recovery) {
            void identity.acceptRankedRecovery(recovery.matchId).then(authorization =>
              startGame(config, undefined, undefined, authorization)).catch(callbacks.onError);
          } else void startGame(config, spectatorCode, queueTicket);
        });
      }
      const element = document.querySelector<HTMLElement>('#game-error');
      if (!element) return;
      element.textContent = localizedErrorMessage(error);
      element.hidden = false;
    },
  };
  try {
    loadingScreen?.setProgress(0.66, t('common:loading.preparingMatch'));
    if (room) {
      const { RemoteGameSession } = await import('./game/RemoteGameSession');
      if (revision !== viewRevision) { room.dispose(); return; }
      if (!localChatAuthorId) throw new Error('Match chat identity is unavailable.');
      session = new RemoteGameSession(host, definition,
        { audioRuntime, inputBindings: inputBindings.bindings, cameraSettings: cameraSettings.settings, onlineRoom: room, localChatAuthorId: () => localChatAuthorId }, callbacks);
      pendingRoom = null;
    } else {
      const current = identity.snapshot;
      const [{ GameSession }, resolvedGoalExplosionLoadout] = await Promise.all([
        import('./game/GameSession'),
        current.kind === 'authenticated' ? inventoryApi.runtime(viewLifetime.signal) : undefined,
      ]);
      if (revision !== viewRevision) return;
      if (identity.snapshot !== current) throw new Error('Identity changed while preparing the local match.');
      session = new GameSession(host, definition,
        { audioRuntime, musicSelections, inputBindings: inputBindings.bindings, cameraSettings: cameraSettings.settings, resolvedGoalExplosionLoadout }, callbacks);
    }
  } catch (error) {
    room?.dispose();
    if (revision === viewRevision) callbacks.onError(error instanceof Error ? error : new Error(String(error)));
    return;
  }
  if (session.interaction) {
    sessionMenu = new SessionMenu(gameScreen, session.interaction, () => {
      const activeRoom = room;
      const participant = activeRoom?.welcome.room.participants.find(item => item.id === activeRoom.welcome.participantId);
      const runningPlayer = activeRoom?.welcome.room.phase === 'running' && participant?.role === 'player';
      const actionKind = activeRoom ? matchSessionActionKind({
        runningPlayer: Boolean(runningPlayer),
        mode: activeRoom.welcome.room.settings.mode,
        forfeitAvailable: onlineHud?.forfeitAvailable ?? false,
        hasVotedForfeit: onlineHud?.hasVotedForfeit ?? false,
      }) : 'leave';
      const terminalAction = actionKind === 'forfeit'
        ? {
            id: 'forfeit', label: t('online:matchHud.forfeit'), destructive: true,
            disabled: onlineHud?.canForfeit() !== true,
            run: () => {
              if (onlineHud?.requestForfeit()) session?.interaction?.continueFromGesture();
            },
          }
        : {
            id: actionKind,
            label: activeRoom
              ? t(actionKind === 'abandon' ? 'online:matchHud.abandonMatch' : 'online:matchHud.leaveMatch')
              : t('match:setup.backToMainMenu'),
            destructive: true,
            confirmation: runningPlayer ? {
              title: t('online:matchHud.exitTitle'),
              description: t(actionKind === 'abandon'
                ? activeRoom!.welcome.room.settings.mode === 'ranked'
                  ? 'online:matchHud.exitWarning.ranked'
                  : activeRoom!.welcome.room.settings.mode === 'casual'
                    ? 'online:matchHud.exitWarning.casual'
                    : 'online:matchHud.exitWarning.leave'
                : 'online:matchHud.exitWarning.leave'),
            } : undefined,
            run: explicitExit,
          };
      return {
        title: t('match:sessionMenu.title'),
        description: activeRoom ? t('match:sessionMenu.onlineRunning') : t('match:sessionMenu.localPaused'),
        terminalAction,
      };
    });
  }
  if (room) loadingScreen?.setIndeterminate(t('common:loading.waitingForServer'));
  else loadingScreen?.setProgress(0.86, t('common:loading.preparingMatch'));
  void session.start();
};

const updateHud = (state: MatchState, focusedEntityId: EntityId | null): void => {
  const blue = document.querySelector('#score-blue');
  const orange = document.querySelector('#score-orange');
  const boost = document.querySelector('#boost');
  const boostMeter = document.querySelector<HTMLElement>('#boost-meter');
  const boostProgress = document.querySelector<SVGCircleElement>('#boost-progress');
  const speed = document.querySelector('#speed');
  const goalBanner = document.querySelector<HTMLElement>('#goal-banner');
  const kickoffCountdown = document.querySelector<HTMLElement>('#kickoff-countdown');
  const focusedVehicle = focusedEntityId === null
    ? undefined
    : state.vehicles.find((vehicle) => vehicle.entityId === focusedEntityId);
  if (blue) blue.textContent = formatNumber(state.score.blue);
  if (orange) orange.textContent = formatNumber(state.score.orange);
  const player = focusedVehicle?.car;
  const boostLevel = player ? Math.max(0, Math.min(100, player.boost)) : 0;
  if (boost) boost.textContent = player ? formatNumber(Math.round(boostLevel)) : '—';
  if (boostProgress) {
    boostProgress.style.strokeDashoffset = String(
      BOOST_METER_CIRCUMFERENCE * (1 - boostLevel / 100),
    );
  }
  if (boostMeter) boostMeter.setAttribute('aria-valuenow', String(Math.round(boostLevel)));
  if (speed) {
    const simUnitsPerSecond = player ? Math.hypot(player.velocity.x, player.velocity.y, player.velocity.z) : 0;
    speed.textContent = player ? formatNumber(Math.round(simUnitsPerSecond * SIM_UNIT_METERS * 3.6)) : '—';
  }
  if (goalBanner) {
    const team = state.flow.goalTeam;
    goalBanner.hidden = state.flow.phase !== 'goal-celebration' || team === null;
    if (team) {
      goalBanner.textContent = team === 'blue' ? t('match:hud.blueScores') : t('match:hud.orangeScores');
      goalBanner.dataset.team = team;
    } else {
      delete goalBanner.dataset.team;
    }
  }
  if (kickoffCountdown) {
    const cue = state.flow.kickoffCue;
    kickoffCountdown.hidden = cue === null;
    kickoffCountdown.textContent = cue === 'GO' ? t('match:hud.go') : cue === null ? '' : formatNumber(Number(cue));
    kickoffCountdown.dataset.cue = cue === 'GO' ? 'go' : 'count';
  }
};

const updateSpectatorHud = (state: GameSpectatorState): void => {
  const indicator = document.querySelector<HTMLElement>('#spectator-indicator');
  const target = document.querySelector<HTMLElement>('#spectator-target');
  const previous = document.querySelector<HTMLElement>('#spectator-previous');
  const next = document.querySelector<HTMLElement>('#spectator-next');
  if (!indicator || !target || !previous || !next) return;
  indicator.hidden = !state.active;
  if (!state.active) return;
  target.textContent = state.takeoverSecondsRemaining === null || state.takeoverSecondsRemaining === undefined
    ? state.targetName
    : t('match:spectator.takeoverCountdown', {
      count: state.takeoverSecondsRemaining,
      name: state.targetName,
      seconds: formatNumber(state.takeoverSecondsRemaining),
    });
  previous.textContent = state.previousHint;
  next.textContent = state.nextHint;
};

const updateCameraHud = (state: GameCameraState): void => {
  const indicator = document.querySelector<HTMLElement>('#camera-mode-indicator');
  const binding = document.querySelector<HTMLElement>('#camera-mode-binding');
  if (!indicator || !binding) return;
  indicator.hidden = state.mode !== 'ball';
  binding.textContent = state.toggleHint;
};

const updateReplayHud = (state: GameReplayState): void => {
  const overlay = document.querySelector<HTMLElement>('#replay-overlay');
  const gameScreen = document.querySelector<HTMLElement>('.game-screen');
  const scorerCard = document.querySelector<HTMLElement>('#replay-scorer-card');
  const scorer = document.querySelector<HTMLElement>('#replay-scorer');
  const speed = document.querySelector<HTMLElement>('#replay-speed');
  const skip = document.querySelector<HTMLElement>('#replay-skip');
  const binding = document.querySelector<HTMLElement>('#replay-skip-binding');
  const pending = document.querySelector<HTMLElement>('#replay-pending');
  if (!overlay || !gameScreen || !scorerCard || !speed || !skip || !binding || !pending) return;

  overlay.hidden = !state.active;
  gameScreen.classList.toggle('game-screen--replay', state.active);
  if (!state.active) return;
  if (scorer) scorer.textContent = t('match:replay.scored', { name: state.scorerName.toUpperCase() });
  speed.textContent = formatNumber(Math.round(state.speedKph), {
    style: 'unit',
    unit: 'kilometer-per-hour',
    unitDisplay: 'short',
  });
  if (scorer) scorerCard.dataset.team = state.team ?? '';
  skip.dataset.device = state.skipDevice;
  binding.textContent = state.skipHint;
  pending.textContent = state.pendingNames.length > 0 ? state.pendingNames.join(', ') : '—';
};

const updateMatchRosterHud = (state: MatchRosterState): void => {
  const overlay = document.querySelector<HTMLElement>('#match-roster-overlay');
  const teams = document.querySelector<HTMLElement>('#match-roster-teams');
  if (!overlay || !teams) return;

  if (matchRosterDismissTimer !== null) {
    window.clearTimeout(matchRosterDismissTimer);
    matchRosterDismissTimer = null;
  }
  if (matchRosterFadeTimer !== null) {
    window.clearTimeout(matchRosterFadeTimer);
    matchRosterFadeTimer = null;
  }

  if (!state.visible) {
    matchRosterDismissTimer = window.setTimeout(() => {
      overlay.setAttribute('aria-hidden', 'true');
      overlay.classList.add('match-roster-overlay--exiting');
      matchRosterDismissTimer = null;
      matchRosterFadeTimer = window.setTimeout(() => {
        overlay.hidden = true;
        overlay.classList.remove('match-roster-overlay--exiting');
        teams.replaceChildren();
        matchRosterFadeTimer = null;
      }, MATCH_ROSTER_FADE_OUT_MS);
    }, MATCH_ROSTER_RELEASE_HOLD_MS);
    return;
  }

  overlay.hidden = false;
  overlay.classList.remove('match-roster-overlay--exiting');
  overlay.setAttribute('aria-hidden', 'false');

  const fragment = document.createDocumentFragment();
  for (const team of ['blue', 'orange'] as const) {
    const members = state.entries.filter((entry) => entry.team === team);
    if (members.length === 0) continue;
    const section = document.createElement('section');
    section.className = 'match-roster-team';
    section.dataset.team = team;
    const header = document.createElement('header');
    header.className = 'match-roster-team__header';
    const heading = document.createElement('h3');
    const score = document.createElement('strong');
    score.className = 'match-roster-team__score';
    score.textContent = formatNumber(state.score[team]);
    const teamName = document.createElement('span');
    teamName.className = 'match-roster-team__name';
    teamName.textContent = team === 'blue' ? t('match:roster.blue') : t('match:roster.orange');
    heading.append(score, teamName);
    const pingHeading = document.createElement('span');
    pingHeading.textContent = t('match:roster.ping');
    header.append(heading, pingHeading);
    section.append(header);
    for (const member of members) {
      const row = document.createElement('div');
      row.className = 'match-roster-player';
      if (member.isLocal) row.dataset.local = 'true';
      const name = document.createElement('strong');
      name.textContent = member.displayName;
      const ping = document.createElement('span');
      ping.className = 'match-roster-player__ping';
      if (member.latencySource === 'local') {
        ping.textContent = t('match:roster.local');
      } else if (member.latencyMs === null) {
        ping.textContent = '—';
      } else {
        ping.textContent = formatNumber(Math.round(member.latencyMs), {
          style: 'unit',
          unit: 'millisecond',
          unitDisplay: 'short',
        });
        ping.dataset.quality = matchRosterLatencyQuality(member.latencyMs);
      }
      row.append(name, ping);
      section.append(row);
    }
    fragment.append(section);
  }
  teams.replaceChildren(fragment);
};

const bootstrapApplication = async (): Promise<void> => {
  loadingScreen?.show('startup');
  loadingScreen?.setProgress(0.12, t('common:loading.restoringSession'));
  performance.mark?.('app:startup:begin');
  try {
    await identity.initialize();
    await syncPrivateIdentity();
    loadingScreen?.setProgress(0.52, t('common:loading.restoringSession'));

    const returnUrl = new URL(window.location.href);
    const code = returnUrl.searchParams.get('spectate');
    const paymentOrder = returnUrl.searchParams.get('paymentOrder');
    let afterContinue: (() => void) | null = null;

    if (identity.snapshot.kind === 'authenticated' && !identity.snapshot.profile.onboardingComplete) {
      renderOnboarding();
    } else if (paymentOrder && /^[0-9a-f-]{36}$/i.test(paymentOrder) && identity.snapshot.kind === 'authenticated') {
      const current = identity.snapshot;
      const routed = await routePaymentReturn(inventoryApi, paymentOrder, {
        'replay-music': renderMusicLibrary,
        'goal-explosion': renderInventory,
      }, () => identity.snapshot === current);
      if (routed) {
        returnUrl.searchParams.delete('paymentOrder');
        returnUrl.searchParams.delete('checkout');
        window.history.replaceState(null, '', returnUrl);
      } else {
        renderHome();
      }
    } else if (code && /^[A-F0-9]{16}$/i.test(code)) {
      afterContinue = () => { void startGame({ mode: 'exhibition', teamSize: 1, localTeam: null, bots: [] }, code); };
    } else {
      renderHome();
      const revision = viewRevision;
      loadingScreen?.setProgress(0.68, t('common:loading.preparingMenu'));
      if (!await startHomeScene(revision)) {
        if (revision !== viewRevision) return;
        throw new Error('Home scene could not be initialized.');
      }
    }

    loadingScreen?.setProgress(1, t('common:loading.ready'));
    performance.mark?.('app:startup:ready');
    performance.measure?.('app:startup-to-ready', 'app:startup:begin', 'app:startup:ready');
    if (!loadingScreen) {
      afterContinue?.();
      return;
    }
    loadingScreen.setReady(() => {
      void audioRuntime.unlock().catch(() => undefined);
      performance.mark?.('app:startup:continued');
      loadingScreen.hide();
      afterContinue?.();
    });
  } catch (error) {
    const failure = error instanceof Error ? error : new Error(String(error));
    console.error('Application startup failed:', failure);
    if (loadingScreen) loadingScreen.setError(localizedErrorMessage(failure), () => { void bootstrapApplication(); });
    else app.innerHTML = `<main class="shell sub-screen"><p role="alert">${escapeHtml(localizedErrorMessage(failure))}</p></main>`;
  }
};

void bootstrapApplication();
